import { produce } from 'immer';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  LoadingObject,
  LoadingState,
} from '../../../../../common/global/models';
import useMount from '../../../../../common/hooks/useMount';
import Loader from '../../../../../common/widgets/Loader';
import { rewriteImageUrl } from '../../../helpers';
import {
  Category,
  CategoryDetails,
  Dictionary,
  ExclusiveCategoryModel,
} from '../../../models/Categories';
import { resources } from '../../../resources/resources';
import { categoryService } from '../../../services/category';
import FixedBenefitDialog from './BenefitDetailsDialog';
import LoadingSpinner from '../../../../../common/widgets/LoadingSpinner';
import { useState } from 'react';

export default function ExclusiveBenefits() {
  const { t: translate } = useTranslation();

  const [activeBenefitId, setActiveBenefitId] = useState<number | null>(null);
  const [categoriesDetails, setCategoriesDetails] = useState<
    Dictionary<LoadingObject<CategoryDetails>>
  >({});
  const [exclusiveBenefits, setExclusiveBenefits] = useState<
    ExclusiveCategoryModel[] | undefined
  >(undefined);
  const [statusText, setStatusText] = useState<string>('');
  const activeBenefit = _.find(
    exclusiveBenefits,
    c => c.categoryId == activeBenefitId,
  );

  const handleBenefitClick = async (e: any, benefitId: number) => {
    setActiveBenefitId(benefitId);

    if (categoriesDetails[benefitId] != null) {
      return;
    }

    setCategoriesDetails(
      produce(draft => {
        draft[benefitId] = { state: LoadingState.Requested };
      }),
    );

    const result = await categoryService.getCategoryDetails(benefitId);

    if (!result.hasErrors) {
      setCategoriesDetails(
        produce(draft => {
          draft[benefitId] = {
            value: { ...result.data },
            state: LoadingState.Ready,
          };
        }),
      );
    }
  };

  const handleBenefitMouseOver = (category: ExclusiveCategoryModel) => {
    setStatusText(
      `${_.unescape(category.categoryName)} (${category.categoryId})`,
    );
  };

  const handleBenefitMouseOut = () => {
    setStatusText('');
  };

  useMount(abortController => {
    (async () => {
      const result = await categoryService.getExclusiveBenefits({
        signal: abortController?.signal,
      });

      if (!result.hasErrors) {
        setExclusiveBenefits(result.data);
      }
    })();
  });

  return (
    <section className="page-section mb-60">
      <p className="mb-25">
        {translate(resources.myBenefitsPage.exclusiveBenefitsCopy)}
      </p>

      <Loader loading={!exclusiveBenefits} loader={<LoadingSpinner />}>
        <div className="card-list card-list-6 card-list-md-5 card-list-sm-4 card-list-xs-3 card-list-xxs-2">
          {_.map(exclusiveBenefits, category => {
            const imgSrc = rewriteImageUrl(category.image2 || category.image);
            return (
              <a
                className="card category-card"
                key={category.categoryId}
                onClick={e => handleBenefitClick(e, category.categoryId)}
                onMouseOver={() => handleBenefitMouseOver(category)}
                onMouseOut={() => handleBenefitMouseOut()}
              >
                <div className="card-img">
                  <img src={imgSrc} alt={_.unescape(category.categoryName)} />
                </div>
                <div className="card-details">
                  <div className="card-title">
                    {_.unescape(category.categoryName)}
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      </Loader>

      {!_.isNil(activeBenefit) && (
        <FixedBenefitDialog
          onClose={() => setActiveBenefitId(null)}
          benefitDetails={categoriesDetails[activeBenefitId!]}
          benefit={
            {
              id: activeBenefit.categoryId,
              parentId: activeBenefit.parentId,
              name: activeBenefit.categoryName,
              image: activeBenefit.image,
              image2: activeBenefit.image2,
            } as Category
          }
        />
      )}
      <div
        style={{
          position: 'fixed',
          bottom: '0',
          left: '0',
          marginBottom: '1px',
          marginLeft: '5px',
          background: '#d5e4e6',
          color: '#9ea3a3',
          fontSize: 14,
        }}
      >
        {statusText}
      </div>
    </section>
  );
}
